import type { CategorySlug, Variety } from "./types";
import { tierPricePerStem, type PricingTier } from "./tier-pricing";

// PASTE #250: per-client BASE price overrides. Locked in
// CLIENT_PRICING_DECISIONS.md (Jul 6 2026), Model = Option 1: per-client
// base prices now, a promo % engine post-launch. Resolution chain:
//
//   client override  ->  global catalog price   ( -> promo %, later )
//
// Overrides are keyed by a "pricing bucket" (NOT the raw catalog
// category) so the premium Tinted rose and Spray roses stay global while
// standard roses and garden roses can be discounted per client. This
// module is PURE (no server/node imports) so it is safe in the client
// bundle: catalogPricePerStem (used by the client cart's buildLine) and
// the server re-price / invoice all consult the SAME clientPriceOverride,
// which keeps CRIT-1's "client display == server re-derive" guarantee.

// The two overridable buckets. Everything else is global-only.
export type PriceBucket = "standard-rose" | "garden-rose";

// Overrides apply ONLY at these stem lengths (cm). 70cm was enabled for the
// MKT-#19 standard-rose promo; clients WITHOUT a 70 entry (382/316) are
// unaffected because the per-client table lookup below still gates each length.
const OVERRIDABLE_LENGTHS: ReadonlySet<number> = new Set([50, 60, 70]);

// Minimal shape needed to classify a variety - accepts a full Variety or
// any object carrying the three identifying fields.
type Classifiable = Pick<Variety, "category" | "name" | "slug">;

// Classify a variety into an overridable bucket, or null (global-only):
// - standard-rose = `rose` category EXCLUDING tinted / blue-tinted rose.
// - garden-rose   = `garden-spray-rose` category EXCLUDING spray roses.
// The tinted/spray exclusions are matched on slug+name so a future SKU
// named "... Tinted ..." or "Spray Rose ..." is auto-excluded without a
// data edit (Step 0 confirmed: rose-blue-tinted-60 is the only tinted
// SKU; spray-rose-* are the only sprays).
export function priceBucketOf(variety: Classifiable): PriceBucket | null {
  const hay = `${variety.slug} ${variety.name}`.toLowerCase();
  if (variety.category === "rose") {
    return hay.includes("tinted") ? null : "standard-rose";
  }
  if (variety.category === "garden-spray-rose") {
    return hay.includes("spray") ? null : "garden-rose";
  }
  return null;
}

// DATA: the per-client override table. Keyed by qbCustomerId -> bucket ->
// lengthCm -> USD per stem. Adding or editing a client's prices later is
// a table edit here, nothing else. Any client not listed = global.
type BucketPrices = Partial<Record<PriceBucket, Record<number, number>>>;

export const CLIENT_PRICE_OVERRIDES: Record<string, BucketPrices> = {
  // 382 - FLOWERS N' DESIGNS
  "382": {
    "standard-rose": { 50: 0.67, 60: 0.67 },
    "garden-rose": { 50: 0.8, 60: 0.8 },
  },
  // 316 - PALM BEACH WHOLESALE
  "316": {
    "standard-rose": { 50: 0.69, 60: 0.73 },
    "garden-rose": { 50: 0.84, 60: 0.84 },
  },
  // MKT-#19 TEMPORARY PROMO for 445 (ALWAYS IN BLOOM / Gary Steenburgh) REVERTED
  // 2026-08-12 per MKT-#34 (expired 2026-08-07). 445 is back on global pricing
  // (50 $0.79 / 60 $0.89 / 70 $0.99). OVERRIDABLE_LENGTHS still lists 70 - it is
  // harmless with no client carrying a 70cm override (lookup returns null ->
  // global).
};

// The single source of override truth. Returns the per-client per-stem
// price for a variety+length, or null when none applies. Fail-safe by
// construction: an unknown client, an unmapped bucket, a length the client
// has no entry for, or a tinted/spray SKU all return null, so the caller
// falls through to the global catalog price and NEVER mis-bills.
export function clientPriceOverride(
  variety: Classifiable,
  lengthCm: number | null | undefined,
  clientId: string | null | undefined,
): number | null {
  if (!clientId) return null;
  const table = CLIENT_PRICE_OVERRIDES[clientId];
  if (!table) return null;
  if (lengthCm == null || !OVERRIDABLE_LENGTHS.has(lengthCm)) return null;
  const bucket = priceBucketOf(variety);
  if (!bucket) return null;
  const price = table[bucket]?.[lengthCm];
  return typeof price === "number" ? price : null;
}

// Rewrite a variety's per-length prices to the client's overrides so
// every DISPLAY surface (catalog cards, detail price table, cart line)
// shows the price the client will actually be billed. Non-overridable
// lengths (70cm), tinted/spray SKUs, and all other categories are left
// untouched. priceByColor is left as-is (no bucket overlaps a
// priceByColor variety). Returns the SAME reference when nothing changes
// so React/serialization churn stays minimal for global-only clients.
export function applyClientPricing(
  variety: Variety,
  clientId: string | null | undefined,
  tier?: PricingTier | null,
): Variety {
  const hasOverride = !!(clientId && CLIENT_PRICE_OVERRIDES[clientId]);
  // flower_shop / undefined tier is a strict no-op (== global). Only a real
  // tier rewrites beyond the explicit override buckets.
  const tierActive = tier === "wholesaler" || tier === "event";
  // No override AND no active tier -> return the SAME reference, byte-identical
  // to legacy. Every client that exists today hits this branch (no tier set).
  if (!hasOverride && !tierActive) return variety;

  let changed = false;
  const next = variety.pricePerStem.map((entry) => {
    // Explicit override wins over the tier (PASTE #394 resolution order).
    const override = hasOverride
      ? clientPriceOverride(variety, entry.length, clientId)
      : null;
    if (override !== null && override !== entry.usd) {
      changed = true;
      return { length: entry.length, usd: override };
    }
    // No override at this length -> apply the tier price if one exists.
    if (override === null && tierActive) {
      const tp = tierPricePerStem(variety, entry.length, tier, entry.usd);
      if (tp !== null && tp !== entry.usd) {
        changed = true;
        return { length: entry.length, usd: tp };
      }
    }
    return entry;
  });

  // priceByColor is only rewritten for an active tier (overrides never touch a
  // priceByColor variety - the buckets don't overlap one). event = per-color
  // global x 1.10; wholesaler = the flat table value for the SKU.
  let nextByColor = variety.priceByColor;
  if (tierActive && variety.priceByColor) {
    const refLen =
      variety.pricePerStem[0]?.length ?? variety.lengths[0] ?? null;
    let colorChanged = false;
    const rebuilt: Record<string, number> = {};
    for (const [colorKey, usd] of Object.entries(variety.priceByColor)) {
      const tp = tierPricePerStem(variety, refLen, tier, usd);
      if (tp !== null && tp !== usd) {
        colorChanged = true;
        rebuilt[colorKey] = tp;
      } else {
        rebuilt[colorKey] = usd;
      }
    }
    if (colorChanged) {
      changed = true;
      nextByColor = rebuilt;
    }
  }

  if (!changed) return variety;
  return { ...variety, pricePerStem: next, priceByColor: nextByColor };
}

// Batch helper for the price-gated serialization boundaries (catalog
// page, detail page, /api/catalog/*). Mirrors stripVarietyPricesFor's
// shape. clientId null (global-only client / signed-in-no-QB) is a no-op.
export function applyClientPricingFor(
  varieties: Variety[],
  clientId: string | null | undefined,
  tier?: PricingTier | null,
): Variety[] {
  const hasOverride = !!(clientId && CLIENT_PRICE_OVERRIDES[clientId]);
  const tierActive = tier === "wholesaler" || tier === "event";
  if (!hasOverride && !tierActive) return varieties;
  return varieties.map((v) => applyClientPricing(v, clientId, tier));
}

// Re-exported for callers that need the category union without importing
// types just for the classifier.
export type { CategorySlug };
